/**
 * CMDB / Elements Connect options often carry an `attributes` array.
 * This util normalizes access to common attributes by id.
 */

import { useTranslation } from 'react-i18next';

const normalizeId = (v) => String(v ?? '');
const normalizeStockValue = (value) => String(value ?? '').trim();

export const getOptionAttributeFirstDisplayValue = (option, objectTypeAttributeId) => {
  const attrs = option?.attributes;
  if (!Array.isArray(attrs) || !attrs.length) return undefined;

  const wantedId = normalizeId(objectTypeAttributeId);
  const targetAttr = attrs.find((attr) => {
    const attrId = normalizeId(attr?.objectTypeAttributeId ?? attr?.id);
    return attrId === wantedId;
  });

  const firstValue = targetAttr?.objectAttributeValues?.[0];
  return firstValue?.displayValue ?? firstValue?.value ?? firstValue?.referencedObject?.label;
};

// Convention used in this project:
// - 259: "type"
// - 304: "image url"
export const getOptionTypeLabel = (option) => getOptionAttributeFirstDisplayValue(option, '259');
export const getOptionImageUrl = (option) => getOptionAttributeFirstDisplayValue(option, '304');
export const getStockValue = (option) => getOptionAttributeFirstDisplayValue(option, '303');
export const getOptionUniverse = (option) => getOptionAttributeFirstDisplayValue(option, '756');

/**
 * Resolve the stock badge as a structured descriptor (no localized string here,
 * so the numeric value never lands inside an i18n literal):
 * - `null` when no stock attribute is present
 * - `{ empty: true }` when the stock is exhausted
 * - `{ count }` with the numeric quantity otherwise
 * @returns {null | { empty: true } | { count: string }}
 */
export const getStockBadge = (option) => {
  const normalizedStockValue = normalizeStockValue(getStockValue(option));

  if (!normalizedStockValue) {
    return null;
  }

  if (normalizedStockValue === '0') {
    return { empty: true };
  }

  return { count: normalizedStockValue };
};

/**
 * Hook returning helpers that localize the stock badge descriptor.
 * Literal-keyed translation calls only (the count flows through interpolation,
 * never into the literal) so the i18n static-key guard stays green.
 */
export const useStockBadge = () => {
  const { t } = useTranslation();

  const getStockBadgeLabel = (option) => {
    const badge = getStockBadge(option);

    if (!badge) {
      return null;
    }

    if (badge.empty) {
      return t('Out of stock');
    }

    return t('{{count}} in stock', { count: Number(badge.count) });
  };

  const appendStockToOptionLabel = (label, option) => {
    const stockBadgeLabel = getStockBadgeLabel(option);

    if (!stockBadgeLabel) {
      return label;
    }

    return `${label} (${stockBadgeLabel})`;
  };

  return { getStockBadgeLabel, appendStockToOptionLabel };
};

